import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Copy, CheckCircle2, Users as UsersIcon, Link as LinkIcon, KeyRound, Crown, UserCog } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = { name: "", email: "", phone: "", cargo: "", client_role: "GESTOR" };

export function ClientUsersManager({ clientId }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/users`);
      setUsers(r.data);
    } catch (e) { toast.error("Erro ao carregar usuários"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId]);

  const openNew = () => { setForm(emptyForm); setOpen(true); };

  const save = async () => {
    try {
      const r = await api.post(`/clients/${clientId}/users`, form);
      setUsers((p) => [...p, r.data]);
      setInviteInfo({ name: r.data.name, email: r.data.email, invite_url: r.data.invite_url });
      setInviteCopied(false);
      setOpen(false);
      toast.success("Usuário criado. Convite gerado.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar usuário"); }
  };

  const changeRole = async (u, client_role) => {
    try {
      const r = await api.patch(`/clients/${clientId}/users/${u.id}`, { client_role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? r.data : x)));
      toast.success("Perfil atualizado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleActive = async (u) => {
    try {
      const r = await api.patch(`/clients/${clientId}/users/${u.id}`, { is_active: !u.is_active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? r.data : x)));
      toast.success(r.data.is_active ? "Usuário ativado" : "Usuário desativado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const resetPassword = async (u) => {
    try {
      const r = await api.post(`/clients/${clientId}/users/${u.id}/reset-password`);
      setInviteInfo({ name: u.name, email: u.email, invite_url: r.data.invite_url });
      setInviteCopied(false);
      toast.success("Link de redefinição gerado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const copyInvite = async () => {
    if (!inviteInfo?.invite_url) return;
    try {
      await navigator.clipboard.writeText(inviteInfo.invite_url);
      setInviteCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setInviteCopied(false), 2500);
    } catch (_e) { void _e; toast.error("Falha ao copiar"); }
  };

  return (
    <div className="space-y-4" data-testid="client-users-manager">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold flex items-center gap-2"><UsersIcon size={16} className="text-primary" /> Usuários da empresa</h3>
        <Button onClick={openNew} size="sm" className="gap-2" data-testid="new-client-user-btn">
          <Plus size={15} /> Novo usuário
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Nenhum usuário cadastrado.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const isSelf = u.id === me?.id;
                  return (
                    <TableRow key={u.id} data-testid={`client-user-row-${u.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {u.client_role === "DONO" ? <Crown size={14} className="text-primary" /> : <UserCog size={14} className="text-muted-foreground" />}
                          {u.name}
                          {isSelf && <span className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted px-1.5 py-0.5 rounded">você</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{u.email}</p>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{u.phone || "-"}</TableCell>
                      <TableCell className="text-sm">{u.cargo || "-"}</TableCell>
                      <TableCell>
                        <select
                          value={u.client_role || "GESTOR"}
                          onChange={(e) => changeRole(u, e.target.value)}
                          disabled={isSelf}
                          data-testid={`client-user-role-${u.id}`}
                          className="h-8 rounded-md border bg-background px-2 text-xs"
                        >
                          <option value="DONO">Dono</option>
                          <option value="GESTOR">Gestor</option>
                        </select>
                      </TableCell>
                      <TableCell>
                        <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} disabled={isSelf} data-testid={`client-user-toggle-${u.id}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => resetPassword(u)} title="Redefinir senha" data-testid={`client-user-reset-${u.id}`}>
                          <KeyRound size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo usuário</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-user-form-name" />
            </div>
            <div>
              <Label className="text-xs">E-mail *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-user-form-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Telefone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-user-form-phone" />
              </div>
              <div>
                <Label className="text-xs">Cargo</Label>
                <Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} data-testid="client-user-form-cargo" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Perfil</Label>
              <select value={form.client_role} onChange={(e) => setForm({ ...form, client_role: e.target.value })}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm mt-1" data-testid="client-user-form-role">
                <option value="DONO">Dono</option>
                <option value="GESTOR">Gestor</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!form.name || !form.email} data-testid="client-user-form-save">Criar e enviar convite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LinkIcon size={18} className="text-primary" /> Link de acesso gerado</DialogTitle>
          </DialogHeader>
          {inviteInfo && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Um link foi gerado para <b className="text-foreground">{inviteInfo.email}</b> criar/redefinir sua senha.
              </p>
              <div className="rounded-lg border bg-muted/40 p-3">
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs break-all font-mono" data-testid="client-user-invite-url">{inviteInfo.invite_url}</code>
                  <Button size="sm" variant="outline" onClick={copyInvite} className="shrink-0 gap-1.5" data-testid="copy-client-user-invite-btn">
                    {inviteCopied ? <><CheckCircle2 size={14} className="text-emerald-500" /> Copiado</> : <><Copy size={14} /> Copiar</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInviteInfo(null)}>Concluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
